<template>
  <AppLayout title="Relatórios">
    <div class="card filter-card mb-4">
      <div class="card-body">
        <div class="d-flex flex-wrap align-items-end gap-3">
          <div class="btn-group" role="group" aria-label="Atalhos de período">
            <button
              v-for="atalho in atalhos"
              :key="atalho.dias"
              type="button"
              class="btn btn-outline-primary"
              :class="{ active: diasSelecionados === atalho.dias }"
              @click="selecionarAtalho(atalho.dias)"
            >{{ atalho.label }}</button>
          </div>

          <div>
            <label class="form-label small text-muted mb-1">De</label>
            <input type="date" class="form-control" v-model="from" @change="periodoManual">
          </div>
          <div>
            <label class="form-label small text-muted mb-1">Até</label>
            <input type="date" class="form-control" v-model="to" @change="periodoManual">
          </div>
        </div>
      </div>
    </div>

    <div v-if="!resumo" class="text-muted">Carregando...</div>

    <div v-else>
      <div class="row">
        <div class="col-md-3 mb-3">
          <div class="card stat-card h-100">
            <div class="card-body">
              <div class="stat-card__icon bg-primary-subtle text-primary"><i class="bi bi-cash-coin"></i></div>
              <h6 class="card-subtitle text-muted mb-2">Vendas no período</h6>
              <h3 class="mb-0">{{ formaterService.currency(resumo.sales.total) ?? 'R$ 0,00' }}</h3>
              <small class="text-muted">{{ resumo.sales.count }} venda(s)</small>
            </div>
          </div>
        </div>
        <div class="col-md-3 mb-3">
          <div class="card stat-card h-100">
            <div class="card-body">
              <div class="stat-card__icon bg-info-subtle text-info"><i class="bi bi-receipt"></i></div>
              <h6 class="card-subtitle text-muted mb-2">Ticket médio</h6>
              <h3 class="mb-0">{{ formaterService.currency(resumo.sales.average_ticket) ?? 'R$ 0,00' }}</h3>
            </div>
          </div>
        </div>
        <div class="col-md-3 mb-3">
          <div class="card stat-card h-100">
            <div class="card-body">
              <div class="stat-card__icon" :class="lucroPositivo ? 'bg-success-subtle text-success' : 'bg-danger-subtle text-danger'">
                <i class="bi" :class="lucroPositivo ? 'bi-piggy-bank' : 'bi-exclamation-triangle'"></i>
              </div>
              <h6 class="card-subtitle text-muted mb-2">Lucro no período</h6>
              <h3 class="mb-0">{{ formaterService.currency(resumo.profit_total) ?? 'R$ 0,00' }}</h3>
              <small class="text-muted">Vendas menos descartes</small>
            </div>
          </div>
        </div>
        <div class="col-md-3 mb-3">
          <div class="card stat-card h-100">
            <div class="card-body">
              <div class="stat-card__icon bg-warning-subtle text-warning"><i class="bi bi-wallet2"></i></div>
              <h6 class="card-subtitle text-muted mb-2">Contas em aberto</h6>
              <h3 class="mb-0">{{ formaterService.currency(resumo.open_accounts.total) ?? 'R$ 0,00' }}</h3>
              <small class="text-muted">{{ resumo.open_accounts.count }} cliente(s)</small>
            </div>
          </div>
        </div>
      </div>

      <div class="row">
        <div class="col-12 mb-3">
          <h2 class="section-title">Vendas por dia</h2>
          <div class="card">
            <div class="card-body">
              <canvas ref="salesChartCanvas" height="90"></canvas>
            </div>
          </div>
        </div>
      </div>

      <div class="row">
        <div class="col-md-6 mb-3">
          <h2 class="section-title">Vendas por forma de pagamento</h2>
          <div class="table-card">
            <table class="table">
              <thead>
                <tr>
                  <th>Forma</th>
                  <th>Qtd.</th>
                  <th>Total</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="metodo in resumo.sales_by_payment_method" :key="metodo.payment_method_id ?? metodo.type">
                  <td>{{ metodo.name }}</td>
                  <td>{{ metodo.count }}</td>
                  <td>{{ formaterService.currency(metodo.total) ?? 'R$ 0,00' }}</td>
                </tr>
                <tr v-if="!resumo.sales_by_payment_method.length">
                  <td colspan="3" class="text-center text-muted">Nenhuma venda no período.</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        <div class="col-md-6 mb-3">
          <h2 class="section-title">Maiores contas em aberto</h2>
          <div class="table-card">
            <table class="table">
              <thead>
                <tr>
                  <th>Cliente</th>
                  <th>Saldo</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="cliente in resumo.open_accounts.customers" :key="cliente.id">
                  <td>{{ cliente.name }}</td>
                  <td>{{ formaterService.currency(cliente.price) ?? 'R$ 0,00' }}</td>
                </tr>
                <tr v-if="!resumo.open_accounts.customers.length">
                  <td colspan="2" class="text-center text-muted">Nenhuma conta em aberto.</td>
                </tr>
              </tbody>
            </table>
          </div>
          <router-link :to="{ name: 'clientes' }" class="btn btn-outline-primary btn-sm mt-3">Ver todos os clientes</router-link>
        </div>
      </div>
    </div>
  </AppLayout>
</template>
<script>
import { computed, nextTick, onBeforeUnmount, onMounted, ref } from 'vue';
import Chart from 'chart.js/auto';
import AppLayout from "@/components/AppLayout.vue";
import { reports } from "@/models/reports";
import formaterService from "@/services/formaterService";

const ATALHOS = [
  { dias: 7, label: '7 dias' },
  { dias: 30, label: '30 dias' },
  { dias: 90, label: '90 dias' },
];

export default {
  name: 'ReportsView',
  components: {
    AppLayout
  },
  setup() {
    const resumo = ref(null);
    const Reports = reports();
    const salesChartCanvas = ref(null);
    let salesChart = null;

    const diasSelecionados = ref(7);
    const from = ref('');
    const to = ref('');

    const lucroPositivo = computed(() => (resumo.value?.profit_total ?? 0) >= 0);

    function paraDataISO(data) {
      return data.toISOString().slice(0, 10);
    }

    function definirPeriodoPorDias(dias) {
      const hoje = new Date();
      const inicio = new Date();
      inicio.setDate(hoje.getDate() - (dias - 1));
      to.value = paraDataISO(hoje);
      from.value = paraDataISO(inicio);
    }

    async function carregar() {
      // Zerar resumo esconde o <canvas> (troca pro "Carregando..."), o que
      // destrói o elemento do gráfico anterior — sem destruir a instância
      // do Chart.js junto, a próxima renderização tentaria atualizar um
      // gráfico preso a um canvas que não existe mais, e nada aparece.
      salesChart?.destroy();
      salesChart = null;
      resumo.value = null;
      resumo.value = await Reports.summary(from.value, to.value);
      if (resumo.value?.sales_by_day) {
        await nextTick();
        renderSalesChart(resumo.value.sales_by_day);
      }
    }

    function selecionarAtalho(dias) {
      diasSelecionados.value = dias;
      definirPeriodoPorDias(dias);
      carregar();
    }

    function periodoManual() {
      diasSelecionados.value = null;
      carregar();
    }

    // formaterService.date() usa `new Date('YYYY-MM-DD')`, que o JS interpreta
    // como meia-noite UTC — em fusos atrás do UTC (ex.: Brasil) isso exibiria
    // o dia anterior. Aqui a data já vem sem horário, então formatamos
    // localmente sem passar pelo construtor Date.
    function labelDoDia(isoDate) {
      const [ano, mes, dia] = isoDate.split('-');
      return `${dia}/${mes}`;
    }

    // formaterService.currency() retorna null para 0 (usado nas telas com
    // `?? 'R$ 0,00'`); no gráfico precisamos do valor real formatado mesmo
    // quando for zero.
    function moeda(valor) {
      return (valor ?? 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
    }

    function renderSalesChart(salesByDay) {
      if (!salesChartCanvas.value) return;

      const labels = salesByDay.map(dia => labelDoDia(dia.date));
      const totals = salesByDay.map(dia => dia.total);

      if (salesChart) {
        salesChart.data.labels = labels;
        salesChart.data.datasets[0].data = totals;
        salesChart.update();
        return;
      }

      salesChart = new Chart(salesChartCanvas.value, {
        type: 'line',
        data: {
          labels,
          datasets: [{
            label: 'Vendas',
            data: totals,
            borderColor: '#0d6efd',
            backgroundColor: 'rgba(13, 110, 253, 0.15)',
            tension: 0.2,
            fill: true,
          }],
        },
        options: {
          responsive: true,
          plugins: {
            legend: { display: false },
            tooltip: {
              callbacks: {
                label: (context) => moeda(context.parsed.y),
              },
            },
          },
          scales: {
            y: {
              beginAtZero: true,
              ticks: {
                callback: (value) => moeda(value),
              },
            },
          },
        },
      });
    }

    onMounted(async () => {
      definirPeriodoPorDias(diasSelecionados.value);
      await carregar();
    });

    onBeforeUnmount(() => {
      salesChart?.destroy();
    });

    return {
      resumo, formaterService, salesChartCanvas, lucroPositivo,
      atalhos: ATALHOS, diasSelecionados, from, to,
      selecionarAtalho, periodoManual,
    }
  },
}
</script>
<style scoped>
.section-title {
  font-size: 1.05rem;
  font-weight: 600;
  color: var(--text-strong);
  margin-bottom: 0.75rem;
}

.stat-card__icon {
  width: 2.5rem;
  height: 2.5rem;
  border-radius: 0.65rem;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.15rem;
  margin-bottom: 0.75rem;
}

.filter-card input[type="date"] {
  max-width: 170px;
}
</style>
